/** @format */
/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Select,
  Option,
  Icon,
  Table,
  TableColumn,
  Pagination,
  Tag,
  Message,
  dayjs
} from '@king-design/react'
import QuickRangeDate from 'src/components/QuickRangeDate'
import { MonitorSearch, DeleteConfirm, TablePage, EllipsisTwoLines } from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { reciverScheme } from 'src/pages/PrometheusMonitor/Alarm/constants'
import { useHistory } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlertNotific, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'
import { merge, unset } from 'lodash'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
  RecordType: 0 | 1 | 2
  Time: [number, number] | []
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  RecordType: 0,
  Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()],
  ...initPage
}

const NotificationTypeEnum = {
  0: '全部',
  1: '触发',
  2: '恢复'
}
const PrometheusAlarmNotificRecord: FC<PrometheusAlarmTabProps> = ({
  className,
  region,
  instances
}) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>({
    ...initSearch,
    Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()]
  })
  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      const mergeParams = merge({}, search, params)
      mergeParams.StartTime = mergeParams.Time[0]
      mergeParams.EndTime = mergeParams.Time[1]
      mergeParams.Region = region
      unset(mergeParams, 'Time')
      if (mergeParams.StartTime && mergeParams.EndTime) {
        const result = await getPrometheusAlertNotific(mergeParams)
        return {
          data: result?.Records || [],
          total: result?.TotalCount || 0
        }
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=notific&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error('策略不存在')
      }
    } else {
      Message.error('策略ID为空')
    }
  }

  return (
    <div className={className}>
      <div className="search_row">
        <div className="search_item">
          <p className="search_item_label">发送时间</p>
          <QuickRangeDate
            realoadIcon={false}
            format="YYYY-MM-DD HH:mm"
            optionalDateRange={90}
            dateDiff={31}
            defautlValue={search.Time}
            width="290px"
            quickRange={[
              '近5分钟',
              '近0.5小时',
              '近1小时',
              '近3小时',
              '近1天',
              '近3天',
              '近1周',
              '近30天'
            ]}
            changeValue={date => {
              setSearch({
                ...search,
                Page: 1,
                Time: date
              })
            }}
          />
          <Icon
            className="ion-refresh cursor-pointer"
            onClick={() => {
              setSearch({
                ...search,
                Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()],
                Page: 1
              })
            }}
          />
        </div>
        <div className="search_item">
          <p className="search_item_label">关联资源</p>
          <Select
            value={search.Instance}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                Instance: value
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">通知类型</p>
          <Select
            value={search.RecordType}
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                RecordType: value
              })
            }}
          >
            {Object.entries(NotificationTypeEnum).map(([value, name]) => {
              return (
                <Option key={value} value={parseInt(value)}>
                  {name}
                </Option>
              )
            })}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">策略名称</p>
          <MonitorSearch
            placeholder="请输入策略，支持模糊搜索"
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </div>
      <Table
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title="关联资源"
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <p className="ellipsis_two_lines table_text">{data.InstanceID}</p>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title="策略 / 规则"
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn
          key="RecordType"
          title="通知类型"
          width="80"
          slotTemplate={([data]) => {
            return data.RecordType === 1 ? (
              <Tag type="danger">触发</Tag>
            ) : (
              <Tag type="default">恢复</Tag>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title="告警摘要"
          slotTemplate={([data]) => {
            return <EllipsisTwoLines title="告警摘要">{data.Summary}</EllipsisTwoLines>
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title="告警内容"
          slotTemplate={([data]) => {
            return <EllipsisTwoLines title="告警内容">{data.Description}</EllipsisTwoLines>
          }}
        ></TableColumn>

        <TableColumn
          key="Recipients"
          title="通知对象"
          width="120"
          slotTemplate={([data]) => {
            return data.Recipients?.length || data.WebHooks?.length ? (
              <div className="ksc-space">
                {data.Recipients ? (
                  <DeleteConfirm
                    title="联系人"
                    content={
                      <>
                        <TablePage
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: reciverScheme,
                            data: data.Recipients
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>联系人</a>
                  </DeleteConfirm>
                ) : null}
                {data.WebHooks ? (
                  <DeleteConfirm
                    title="回调"
                    content={
                      <>
                        {data.WebHooks.map((url, index) => (
                          <p key={url + index}>{url}</p>
                        ))}
                      </>
                    }
                  >
                    <a>回调</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn key="NotifiedAt" title="发送时间" width="160"></TableColumn>
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={tableData?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch({
            ...search,
            Page: value
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(PrometheusAlarmNotificRecord)`
  .ion-refresh {
    margin-left: 4px;
  }
`
